import { LogLevel, Logger } from '../../../../Arshu/dist/logger/logger.js';
import { JsonUtility } from '../../../../Assembler/dist/app/jsonUtility.js';
import { JsonObject } from '../../../../Assembler/dist/app/json/jsonValue.js';
import { ResourceStatistics } from '../../../../Assembler/dist/model/index.js';
import { ILoaderPreProcessJson } from './iLoaderPreProcessJson.js';
import type { GetTemplateResult } from './iLoaderPreProcessJson.js';
import { ParseHtmlJson } from '../../base/loader/parseHtmlJson.js';
import { GenerateHtmlJson } from '../../base/loader/generateHtmlJson.js';
import { GeneratePreProcessJsonMetadata } from './generatePreProcessJsonMetadata.js';
import { AppSiteMetadata, JsonKeyEntryInfo, PreProcessTemplateStoreJson, SourceFile, TargetFile, TemplateEntryInfo, TemplateInfo, SlotInfo, SlotContentInfo } from '../../model/index.js';
import { DynamicResultsJson } from '../../model/dynamicResultsJson.js';
import { ArtifactKind } from '../../../../Assembler/dist/config/iConfig.js';
import type { IConfig } from '../../../../Assembler/dist/config/iConfig.js';
import { DynamicConfigMap } from '../../../../Assembler/dist/registry/dynamicConfigMap.js';
import type { InheritanceOperation, NavigationDecision } from '../../../../Assembler/dist/model/index.js';
import { TransientStore } from '../../../../Assembler/dist/registry/transientStore.js';
import type { ITransientStore } from '../../../../Assembler/dist/registry/iface/iTransientStore.js';
import type { AppSiteResult } from '../../../../Assembler/dist/registry/iface/appSiteResult.js';

// Import from split modules
import {
    getTemplateDataWithInheritance,
} from './loaderPreProcessJson_Inheritance.js';

import {
    dynamicMethods
} from './loaderPreProcessJson_Dynamic.js';

import { CommonUtil, replaceCaseInsensitive } from '../../../../Assembler/dist/common/commonUtil.js';

// Import ILoader methods from split file (matching C# partial class pattern)
import { iloaderMethods } from './loaderPreProcessJson_ILoader.js';

import { lifecycleMethods } from './loaderPreProcessJson_Lifecycle.js';
import { accessorMethods, accessorStaticMethods } from './loaderPreProcessJson_Accessors.js';

type PlainJsonObject = { [key: string]: unknown };

/**
 * Loader that implements ILoaderPreProcessJson for PreProcess engine using ProcessMain architecture
 * Generates HtmlJson and AppSiteMetadata during loading phase
 */
export class LoaderPreProcessJson extends ILoaderPreProcessJson {
    // Instance-level store - separates loaded data from per-merge mutable state
    _store: PreProcessTemplateStoreJson = new PreProcessTemplateStoreJson();

    // Backward-compatible accessors that delegate to the store
    get _htmlJsonCache(): Map<string, Map<string, TargetFile>> { return this._store.htmlJsonCache; }
    get _dataJsonCache(): Map<string, Map<string, TargetFile>> { return this._store.dataJsonCache; }
    get _metadataCache(): Map<string, AppSiteMetadata> { return this._store.metadataCache; }
    get _resourceStatsCache(): Map<string, ResourceStatistics> { return this._store.resourceStatsCache; }

    _sourceJsonCache: Map<string, Map<string, TargetFile>> = new Map();

    _config: IConfig | null = null;
    _appSite: string = '';

    // Configuration
    genMissingMetaInfo: boolean = false;

    // Track Inheritance operations for flow data
    _inheritanceOperations: InheritanceOperation[] = [];

    // Per-merge mutable state for dynamic handler results
    _dynamicResultsJson: DynamicResultsJson = new DynamicResultsJson();
    _transientStore: ITransientStore = new TransientStore();

    // DynamicData timing instrumentation - in nanoseconds
    _dynamicDataNanos: number = 0;

    // DynamicView timing instrumentation - in nanoseconds
    _dynamicViewNanos: number = 0;
    _dynamicViewCount: number = 0;
    _dynamicDataCount: number = 0;

    // DynamicClientData timing instrumentation - in nanoseconds (F4)
    _dynamicClientDataNanos: number = 0;
    _dynamicClientDataCount: number = 0;

    // Skip patterns for DynamicView/DynamicData handlers
    _skipDynamicView: string | null = null;
    _skipDynamicData: string | null = null;

    _dynamicActionNanos: number = 0;
    _dynamicActionCount: number = 0;
    _actionMode: boolean = false;

    // Pre-loaded stores (populated during Load, used during engine processing)
    _dynamicDataConfigStore: Record<string, DynamicConfigMap | null> = {};
    _dynamicClientDataConfigStore: Record<string, DynamicConfigMap | null> = {};
    _dynamicViewConfigStore: Record<string, DynamicConfigMap | null> = {};
    _dynamicActionConfigStore: Record<string, DynamicConfigMap | null> = {};
    _dynamicAppSiteConfigStore: Record<string, DynamicConfigMap | null> = {};
    _dynamicDataParamsStore: JsonObject = new JsonObject();
    _dynamicClientDataParamsStore: JsonObject = new JsonObject();
    _dynamicViewParamsStore: JsonObject = new JsonObject();
    _dynamicActionParamsStore: JsonObject = new JsonObject();
    _dynamicAppSiteParamsStore: JsonObject = new JsonObject();

    // Per-merge request context (stashed at merge top from applyDynamicAppSiteHandlers). Reserved for
    // the AppSite/authorization phase (later phase). Mirrors the C# loader request context.
    _requestContextJson: JsonObject | null = null;
    // Per-merge navigation trace (accumulated by applyDynamicAppSiteHandlers; drained by the engine into
    // MergeResult.navigationTrace via getAndClearNavigationTrace). Mirrors _inheritanceOperations ownership.
    _lastNavigationTrace: NavigationDecision[] = [];
    _globalDataStore: Record<string, JsonObject | null> = {};
    _globalViewDataStore: Record<string, JsonObject | null> = {};
    _globalViewDataComponentStore: Record<string, JsonObject | null> = {};

    // #region Mixin Method Declarations
    // Methods attached via Object.assign from mixin files - declared with definite assignment

    // From loaderPreProcessJson_ILoader.ts (iloaderMethods)
    declare load: (config: IConfig, appSite: string) => boolean;
    declare clearCache: () => void;
    declare getAppFile: (appSite: string, appView: string) => { appFile: string; validatedAppView: string };
    declare serializeDynamicConfigToJson: (config: DynamicConfigMap) => string;
    declare deserializeStringList: (json: JsonObject, key: string) => string[];
    declare deserializeSlotInfoList: (values: unknown) => unknown[];
    declare deserializeTemplateInfo: (raw: JsonObject) => TemplateInfo;
    declare deserializeAppSiteMetadata: (raw: JsonObject) => AppSiteMetadata;
    declare buildMetadataJsonForAppSite: (appSite: string) => string;
    declare getTemplatesApiResponse: (appSite: string, appView: string, contextJson: JsonObject, serverTimeMs: number, includeSearchAppSites?: boolean, maskHtml?: boolean, orderedTemplateNames?: string[] | null) => string;
    declare setTransientStore: (transientStore: ITransientStore) => void;
    declare getTransientStore: () => ITransientStore;
    declare getComponentId: (appSite: string, componentName: string) => string | null;
    declare setDynamicResults: (dynamicResults: DynamicResultsJson) => void;
    declare getDynamicDataConfig: (appSite: string) => DynamicConfigMap | null;
    declare getDynamicDataParams: (appSite: string, paramFile: string) => JsonObject | null;
    declare getDynamicClientDataConfig: (appSite: string) => DynamicConfigMap | null;
    declare getDynamicClientDataParams: (appSite: string, paramFile: string) => JsonObject | null;
    declare tryResolvePureClientHandlerParams: (appSite: string, componentName: string, clientHandlerName: string) => JsonObject | null;
    declare getDynamicViewConfig: (appSite: string) => DynamicConfigMap | null;
    declare getDynamicViewParams: (appSite: string, paramFile: string) => JsonObject | null;
    declare getDynamicActionParams: (appSite: string, paramFile: string) => JsonObject | null;
    declare getDynamicAppSiteConfig: (appSite: string) => DynamicConfigMap | null;
    declare getDynamicAppSiteParams: (appSite: string, paramFile: string) => JsonObject | null;
    declare ensureAppSiteLoaded: (appSite: string) => boolean;
    declare applyDynamicAppSiteHandlers: (appSite: string, appView: string | null, contextJson: JsonObject, configJson: JsonObject | null) => AppSiteResult;
    declare getGlobalData: (appSite: string) => JsonObject | null;
    declare getGlobalViewData: (appSite: string) => JsonObject | null;
    declare getGlobalViewDataForComponent: (appSite: string, componentName: string) => JsonObject | null;
    declare _preloadDynamicAndGlobalData: (config: IConfig, appSite: string) => void;
    declare _serializePerSiteStores: (sb: string[], appSite: string) => void;
    declare _serializePerSiteDynamicConfig: (sb: string[], allSites: string[], key: string, getter: (site: string) => DynamicConfigMap | null) => void;
    declare _serializePerSiteParamsFromStore: (sb: string[], allSites: string[], key: string, paramsStore: JsonObject) => void;

    // From loaderPreProcessJson_Dynamic.ts (dynamicMethods)
    declare hasDynamicDataHandlers: (appSite: string, componentName: string) => boolean;
    declare hasDynamicActionHandlers: (appSite: string, componentName: string) => boolean;
    declare getTemplateWithView: (appSite: string, currentTemplateName: string, componentName: string, appView?: string | null, flowStack?: string[] | null, configJson?: JsonObject | null, componentDepthOverride?: number | null, clientMode?: boolean) => GetTemplateResult;
    declare tryBuildParentJsonValueHandlerForAction: (appSite: string, flowStack: string[] | null, contextJson?: JsonObject | null) => ((key: string) => string | null) | null;
    declare executeWithReadOnly: <T>(paramJson: JsonObject, componentJson: JsonObject, configJson: JsonObject | null, action: () => T) => T;
    declare applyDynamicDataHandlers: (appSite: string, appView: string, appFile: string, componentName: string, componentDepth: number, resolvedAppSite: string, resolvedComponentName: string, componentJson: JsonObject | PlainJsonObject, configJson?: JsonObject | null, flowStack?: string[] | null) => void;
    declare applyDynamicClientDataHandlers: (appSite: string, appView: string, appFile: string, componentName: string, componentDepth: number, resolvedAppSite: string, resolvedComponentName: string, componentJson: JsonObject, configJson: JsonObject | null, flowStack?: string[] | null) => void;
    declare reconstructComponentHtmlFromJson: (htmlJson: JsonObject | null) => string;
    declare applyClientHandlers: (appSite: string, appView: string, appFile: string, componentName: string, componentDepth: number, resolvedAppSite: string, resolvedComponentName: string, htmlJson: JsonObject | null, resolvedJson: JsonObject, configJson: JsonObject | null, flowStack?: string[] | null) => void;
    declare getClientDataSlice: (appSite: string, handlerName: string, callerParams: JsonObject) => JsonObject | null;
    declare getClientDataIslands: (appSite: string, callerParams: JsonObject) => JsonObject;
    declare hasClientData: (appSite: string) => boolean;
    declare applyDynamicActionHandlers: (appSite: string, appView: string, appFile: string, componentName: string, resolvedAppSite: string, resolvedComponentName: string, componentJson: JsonObject | PlainJsonObject, configJson?: JsonObject | null, flowStack?: string[] | null) => void;
    declare executeDynamicAction: (appSite: string, appView: string, appFile: string, componentName: string, resolvedAppSite: string, resolvedComponentName: string, paramJson: PlainJsonObject, componentJson: JsonObject | PlainJsonObject, actionJson: JsonObject | null, actionHandler: import('../../../../Assembler/dist/registry/iface/iAction.js').IAction, getParentJsonValue: ((key: string) => string | null) | null) => void;
    declare applyDynamicViewHandlersJson: (appSite: string, appView: string, appFile: string, componentName: string, componentDepth: number, componentHtmlJson: JsonObject | null, componentJson: JsonObject | PlainJsonObject | null, resolvedAppSite: string, resolvedComponentName: string, configJson?: JsonObject | null) => JsonObject | null;
    declare _applyDynamicViewHandlersJson: (appSite: string, appView: string, appFile: string, componentName: string, componentDepth: number, componentHtmlJson: JsonObject, componentJson: JsonObject | PlainJsonObject | null, resolvedAppSite: string, resolvedComponentName: string, configJson?: JsonObject | null) => JsonObject;
    declare resolveDynamicDataConfigSource: (appSite: string) => { config: DynamicConfigMap | null; configAppSite: string };
    declare getDynamicDataMatchKey: (dynamicConfig: DynamicConfigMap, componentName: string) => string | null;
    declare resolveDynamicActionConfigSource: (appSite: string) => { config: DynamicConfigMap | null; configAppSite: string };
    declare getDynamicActionMatchKey: (dynamicConfig: DynamicConfigMap, componentName: string) => string | null;
    declare syncDynamicActionResults: (componentName: string, afterJson: unknown) => void;
    declare _applyDynamicDataHandlersSync: (appSite: string, componentName: string, componentJson: PlainJsonObject) => void;
    declare _convertJsonArrayToPlain: (jsonArray: import('../../../../Assembler/dist/app/json/jsonValue.js').JsonArray) => unknown[];
    declare _convertJsonObjectToPlain: (jsonObject: JsonObject) => PlainJsonObject;
    declare _unwrapJsonValue: (jsonValue: import('../../../../Assembler/dist/app/json/jsonValue.js').JsonValue) => unknown;

    // From loaderPreProcessJson_Accessors.ts (actionAccessorMethods)
    declare getDynamicActionConfig: (appSite: string) => DynamicConfigMap | null;
    declare getDynamicActionTiming: () => { nanos: number; count: number };
    declare getDynamicViewTiming: () => { nanos: number; count: number };
    declare getDynamicClientDataTiming: () => { nanos: number; count: number };
    declare setActionMode: (actionMode: boolean) => void;
    declare getActionMode: () => boolean;
    declare readonly defaultReplaceTemplateName: string;
    declare readonly defaultSkipTemplateName: string;
    declare readonly defaultReplaceHtml: string;
    declare readonly defaultSkipHtml: string;
    declare readonly defaultSkipJson: string;
    declare readonly maxArrayOperationItems: number;
    declare readonly maxRecursionDepth: number;
    declare readonly maxMergeIterations: number;

    // From loaderPreProcessJson_Accessors.ts (accessorMethods/accessorStaticMethods)
    declare populateExecutionMetadata: () => void;
    declare populateExecutionMetadataRecursive: (component: TemplateInfo) => void;
    declare buildComponentIndex: () => void;
    declare addComponentToIndex: (component: TemplateInfo, indexPrefix: string) => void;
    declare getSearchAppSitesForAppSite: (appSite: string) => string;
    declare hasTemplate: (appSite: string, currentTemplateName: string, appFileOrPlaceholderOrSlotholderName: string, appView?: string | null) => boolean;
    declare populateDynamicHandlerFlags: (component: TemplateInfo, config: LoaderPreProcessJson | IConfig, appSite: string) => void;
    declare getResourceStatistics: (appSite: string) => ResourceStatistics;
    declare computeResourceStatistics: (appSite: string) => ResourceStatistics;
    declare shouldSkipHandler: (handlerType: string, skipPatterns: string | Set<string> | null) => boolean;
    declare getLoaderStateJson: (appSite: string) => string;
    declare getLoaderPerSiteStoresJson: (appSite: string) => string;
    // #endregion

    static buildAppSitesToLoad: (appSite: string, searchAppSites: string) => string[];
    static loadTemplatesFromSingleAppSite: (config: IConfig, appSiteName: string, sourceHtmlCache: Map<string, Map<string, SourceFile>>, sourceJsonCache: Map<string, Map<string, TargetFile>>) => void;
    static countJsonKeysAndArrays: (json: PlainJsonObject | JsonObject, stats: ResourceStatistics) => void;
    static countHandlersInConfig: (config: DynamicConfigMap | PlainJsonObject) => number;
    static dumpHtmlJsonDebug: (sb: string[], dict: JsonObject, indent: number) => void;

    constructor(config: IConfig, appSite: string) {
        super();
        this._config = config;
        this.load(config, appSite);
    }

    _loadImpl(config: IConfig, appSite: string): boolean {
        try {
            this._config = config;
            this._appSite = appSite;

            const { appFile } = config.getAppFile(appSite, '');
            const searchAppSites = config.getSearchAppSitesForAppSite(appSite);
            if (Logger.getLogLevel() <= LogLevel.DEBUG) {
                Logger.debug(`LoaderPreProcessJson.load: appSite=${appSite}, appFile=${appFile}, searchAppSites=${searchAppSites}`, 'LoaderPreProcessJson');
            }

            // PHASE 1: Load source files from filesystem
            const appSitesToLoad = LoaderPreProcessJson.buildAppSitesToLoad(appSite, searchAppSites);

            if (Logger.getLogLevel() <= LogLevel.DEBUG) {
                Logger.debug(`Loading ${appSitesToLoad.length} AppSites`, 'LoaderPreProcessJson');
            }

            const sourceHtmlCache = new Map<string, Map<string, SourceFile>>();
            const sourceJsonCache = new Map<string, Map<string, TargetFile>>();

            for (const appSiteName of appSitesToLoad) {
                LoaderPreProcessJson.loadTemplatesFromSingleAppSite(config, appSiteName, sourceHtmlCache, sourceJsonCache);
            }

            if (Logger.getLogLevel() <= LogLevel.DEBUG) {
                Logger.debug(`Loaded ${sourceHtmlCache.size} HTML caches and ${sourceJsonCache.size} JSON caches`, 'LoaderPreProcessJson');
            }

            this._sourceJsonCache = sourceJsonCache;

            // PHASE 2: Transfer DataJson from source to cache
            for (const [appSiteName, jsonFiles] of sourceJsonCache.entries()) {
                const targetJsonFiles = new Map<string, TargetFile>();
                for (const [templateName, sourceFile] of jsonFiles.entries()) {
                    const targetFile = new TargetFile();
                    targetFile.relativePath = sourceFile.relativePath;

                    let dataJson: JsonObject | null = null;
                    if (typeof (config as unknown as { getDataJson?: unknown }).getDataJson === 'function') {
                        dataJson = (config as unknown as { getDataJson(s: string, t: string): JsonObject | null }).getDataJson(appSiteName, templateName);
                    }

                    if (dataJson) {
                        targetFile.jsonStructure = dataJson;
                        if (Logger.getLogLevel() <= LogLevel.DEBUG) {
                            Logger.debug(`Using pre-parsed DataJson for ${templateName}`, 'LoaderPreProcessJson');
                        }
                    } else if (sourceFile.jsonStructure) {
                        targetFile.jsonStructure = sourceFile.jsonStructure;
                    }
                    const dataJsonKey = `${appSiteName.toLowerCase()}_${templateName.toLowerCase()}`;
                    targetJsonFiles.set(dataJsonKey, targetFile);
                }
                this._store.setDataJsonTemplates(appSiteName, targetJsonFiles);
            }

            // PHASE 3: Generate HtmlJson + AppSiteMetadata
            const resolvedAppFile = appFile || 'Index';

            const metadataKeys = config.getArtifactNames(appSite, ArtifactKind.Metadata);

            if (metadataKeys.length > 0) {
                for (const metaKey of metadataKeys) {
                    // `metadataKeys` came from this source's OWN getArtifactNames index, so a null
                    // here is the source contradicting itself — never a licence to skip the entry
                    // (which would silently drop the AppSite's execution metadata).
                    const metaBytes = config.getArtifactBytes(appSite, metaKey, ArtifactKind.Metadata);
                    if (!metaBytes) {
                        Logger.fallback('F-ENG7',
                            `Metadata artifact enumerated but not served: appSite=${appSite} contentName=${metaKey}`);
                        throw new Error(
                            `F-ENG7: Metadata artifact missing for '${appSite}/${metaKey}' though the source enumerated it.`);
                    }

                    // Bytes present means the artifact EXISTS, so a decode/parse failure is a corrupt
                    // artifact. parseJsonString would have returned an EMPTY object here and handed it
                    // to _deserializeAppSiteMetadata, fabricating a valid-looking but contentless entry.
                    let metaJsonObj: JsonObject;
                    try {
                        metaJsonObj = JsonUtility.parseJsonStringStrict(JsonUtility.decodeUtf8Strict(metaBytes));
                    } catch (ex) {
                        Logger.fallback('F-ENG6',
                            `Metadata artifact present but undecodable/unparseable: appSite=${appSite} contentName=${metaKey} bytes=${metaBytes.length} ex=${(ex as Error).message}`);
                        throw ex;
                    }
                    const metaPlain = JsonUtility.toPlainObject(metaJsonObj) as PlainJsonObject;
                    const metadata = this._deserializeAppSiteMetadata(metaPlain);
                    const metadataKey = metadata.appView ? `${appSite}_${metadata.appView}` : metaKey;
                    this._store.setMetadata(metadataKey, metadata);
                    if (Logger.getLogLevel() <= LogLevel.DEBUG) {
                        Logger.debug(`Using pre-built metadata for ${metadataKey}`, 'LoaderPreProcessJson');
                    }
                }
            } else {
                // FileConfig - generate metadata from scratch
                const appViews: string[] = [];
                const allContentNames = config.getAllContentNames(appSite);
                for (const contentName of allContentNames) {
                    const relativePath = config.getRelativeInfo(appSite, contentName) || '';
                    if (relativePath.startsWith('Views/') || relativePath.startsWith('Views\\')) {
                        const parts = relativePath.split(/[\\/]/);
                        if (parts.length >= 2) {
                            const viewName = parts[1];
                            if (!appViews.includes(viewName)) {
                                appViews.push(viewName);
                            }
                        }
                    }
                }

                if (Logger.getLogLevel() <= LogLevel.DEBUG) {
                    Logger.debug(`AppFile: ${resolvedAppFile}, AppViews: ${appViews.length}`, 'LoaderPreProcessJson');
                }

                const allTemplates = new Map<string, Map<string, TemplateInfo>>();
                for (const appSiteName of appSitesToLoad) {
                    if (!sourceHtmlCache.has(appSiteName)) continue;

                    const appSiteTemplates = sourceHtmlCache.get(appSiteName)!;
                    const appSiteJsons = sourceJsonCache.get(appSiteName);
                    const templatesMap = new Map<string, TemplateInfo>();

                    for (const [templateName, sourceFile] of appSiteTemplates.entries()) {
                        const templateInfo = ParseHtmlJson.parseTemplateStructure(
                            sourceFile.content,
                            templateName + '.html',
                            appSiteName,
                            templateName
                        );
                        templateInfo.htmlRelativePath = sourceFile.relativePath;

                        if (appSiteJsons && appSiteJsons.has(templateName)) {
                            templateInfo.jsonFileName = templateName + '.json';

                            const jsonSourceFile = appSiteJsons.get(templateName)!;
                            if (jsonSourceFile.jsonStructure) {
                                GeneratePreProcessJsonMetadata.classifyJsonKeys(templateInfo, jsonSourceFile.jsonStructure, sourceFile.content);
                            }
                        }

                        templatesMap.set(templateName, templateInfo);
                    }

                    allTemplates.set(appSiteName, templatesMap);
                }

                if (Logger.getLogLevel() <= LogLevel.DEBUG) {
                    Logger.debug(`Parsed ${allTemplates.size} AppSite templates`, 'LoaderPreProcessJson');
                }

                if (appViews.length === 0) {
                    const baseMetadata = new AppSiteMetadata();
                    baseMetadata.appSiteName = appSite;
                    baseMetadata.appFile = resolvedAppFile;
                    baseMetadata.searchAppSites = searchAppSites;
                    baseMetadata.appView = '';
                    baseMetadata.appViews = appViews;

                    baseMetadata.rootComponent = GeneratePreProcessJsonMetadata.buildAppsiteHierarchy(
                        allTemplates, appSite, resolvedAppFile, this._sourceJsonCache, '', 'Main', config.maxRecursionDepth
                    );

                    GeneratePreProcessJsonMetadata.classifyPlaceholdersRecursive(baseMetadata.rootComponent);

                    baseMetadata.missingInfo = GeneratePreProcessJsonMetadata.collectMissingMetaInfo(
                        baseMetadata.rootComponent, appSite
                    );

                    this._store.setMetadata(appSite, baseMetadata);
                    if (Logger.getLogLevel() <= LogLevel.DEBUG) {
                        Logger.debug(`Generated base metadata for ${appSite}`, 'LoaderPreProcessJson');
                    }
                } else {
                    for (const appViewName of appViews) {
                        const appViewMetadata = new AppSiteMetadata();
                        appViewMetadata.appSiteName = appSite;
                        appViewMetadata.appFile = resolvedAppFile;
                        appViewMetadata.searchAppSites = searchAppSites;
                        appViewMetadata.appView = appViewName;
                        appViewMetadata.appViews = appViews;

                        try {
                            appViewMetadata.rootComponent = GeneratePreProcessJsonMetadata.buildAppsiteHierarchy(
                                allTemplates, appSite, resolvedAppFile, this._sourceJsonCache, appViewName, 'Main', config.maxRecursionDepth
                            );

                            GeneratePreProcessJsonMetadata.classifyPlaceholdersRecursive(appViewMetadata.rootComponent);

                            appViewMetadata.missingInfo = GeneratePreProcessJsonMetadata.collectMissingMetaInfo(
                                appViewMetadata.rootComponent, appSite
                            );

                            const metadataKey = `${appSite}_${appViewName}`;
                            this._store.setMetadata(metadataKey, appViewMetadata);
                            if (Logger.getLogLevel() <= LogLevel.DEBUG) {
                                Logger.debug(`Generated AppView metadata for ${appSite}_${appViewName}`, 'LoaderPreProcessJson');
                            }
                        } catch (ex) {
                            Logger.error(`Failed to build metadata for ${appSite}_${appViewName}: ${(ex as Error).message}`, 'LoaderPreProcessJson');
                            const fallbackRoot = { templateName: resolvedAppFile, components: [] } as unknown as TemplateInfo;
                            appViewMetadata.rootComponent = fallbackRoot;
                            const metadataKey = `${appSite}_${appViewName}`;
                            this._store.setMetadata(metadataKey, appViewMetadata);
                        }
                    }
                }
            }

            // PHASE 4: Generate HtmlJson structures for ALL loaded AppSites
            for (const [appSiteName, htmlFiles] of sourceHtmlCache.entries()) {
                const targetHtmlFiles = new Map<string, TargetFile>();
                for (const [templateName, sourceFile] of htmlFiles.entries()) {
                    const targetFile = new TargetFile();
                    targetFile.relativePath = sourceFile.relativePath;

                    // Bytes present means the artifact EXISTS, so a decode/parse failure is a corrupt
                    // artifact — never a licence to fall through to the server-mode conversion below.
                    const htmlJsonBytes = config.getArtifactBytes(appSiteName, templateName, ArtifactKind.HtmlJson);
                    let htmlJson: JsonObject | null = null;
                    if (htmlJsonBytes) {
                        try {
                            htmlJson = JsonUtility.parseJsonStringStrict(JsonUtility.decodeUtf8Strict(htmlJsonBytes));
                        } catch (ex) {
                            Logger.fallback('F-ENG6',
                                `HtmlJson artifact present but undecodable/unparseable: appSite=${appSiteName} contentName=${templateName} bytes=${htmlJsonBytes.length} ex=${(ex as Error).message}`);
                            throw ex;
                        }
                    }

                    if (htmlJson) {
                        targetFile.jsonStructure = htmlJson;
                        if (Logger.getLogLevel() <= LogLevel.DEBUG) {
                            Logger.debug(`Using pre-parsed HtmlJson for ${templateName}`, 'LoaderPreProcessJson');
                        }
                    } else {
                        // Raw HTML exists, so the artifact generator WOULD have emitted an HtmlJson
                        // for this template. A pre-parsed source missing it is a packaging defect:
                        // rebuilding here would hide the miss AND pay the parse cost the artifact
                        // exists to avoid.
                        if (config.isArtifactSource && sourceFile.content) {
                            Logger.fallback('F-ENG7',
                                `HtmlJson artifact absent from a pre-parsed source: appSite=${appSiteName} contentName=${templateName}`);
                            throw new Error(
                                `F-ENG7: HtmlJson artifact missing for '${appSiteName}/${templateName}' on a pre-parsed source.`);
                        }
                        const htmlJsonStructure = GenerateHtmlJson.convertHtmlToJson(sourceFile.content);
                        targetFile.jsonStructure = htmlJsonStructure.content;
                    }
                    targetHtmlFiles.set(templateName, targetFile);
                }

                this._store.setHtmlJsonTemplates(appSiteName, targetHtmlFiles);
                if (Logger.getLogLevel() <= LogLevel.DEBUG) {
                    Logger.debug(`Generated ${targetHtmlFiles.size} HtmlJson structures for ${appSiteName}`, 'LoaderPreProcessJson');
                }
            }

            if (Logger.getLogLevel() <= LogLevel.DEBUG) {
                Logger.debug(`Load completed - All caches populated`, 'LoaderPreProcessJson');
            }

            this._preloadDynamicAndGlobalData(config, appSite);

            for (const [_metadataKey, metadata] of this._store.getAllMetadata().entries()) {
                if (metadata.rootComponent) {
                    this.populateDynamicHandlerFlags(metadata.rootComponent, this, appSite);
                }
            }

            // PHASE 5: Build component index for O(1) lookups
            this.populateExecutionMetadata();
            this.buildComponentIndex();

            return true;
        } catch (ex) {
            Logger.error(`Failed to load templates: ${(ex as Error).message}`, 'LoaderPreProcessJson');
            Logger.fallback("F-ENG2", `load templates failed: ex=${(ex as Error).message}`);
            throw ex;
        }
    }

    getComponentFromIndex(appSite: string, appView: string | null, componentName: string): TemplateInfo | null {
        const indexKey = appView ? `${appSite}_${appView}_${componentName}` : `${appSite}_${componentName}`;
        return this._store.tryGetComponentInfo(indexKey) ?? null;
    }

    static _convertArrayToDict(arr: Array<PlainJsonObject>): PlainJsonObject {
        const dictObj: PlainJsonObject = {};
        for (const item of arr) {
            if (item && typeof item === 'object' && 'Key' in item) {
                const itemKey = item.Key as string;
                const value = item.Value;
                if (Array.isArray(value)) {
                    const nestedArr: unknown[] = [];
                    for (const nestedItem of value) {
                        if (Array.isArray(nestedItem)) {
                            const nestedDict: PlainJsonObject = {};
                            for (const kvp of nestedItem as Array<PlainJsonObject>) {
                                if (kvp && typeof kvp === 'object' && 'Key' in kvp) {
                                    nestedDict[kvp.Key as string] = kvp.Value;
                                }
                            }
                            nestedArr.push(nestedDict);
                        } else {
                            nestedArr.push(nestedItem);
                        }
                    }
                    dictObj[itemKey] = nestedArr;
                } else {
                    dictObj[itemKey] = value;
                }
            }
        }
        return dictObj;
    }

    getTemplate(
        appSite: string,
        currentTemplateName: string,
        appFileOrPlaceholderOrSlotholderName: string,
        appView: string | null = null,
        flowStack: string[] | null = null,
        configJson: JsonObject | null = null,
        clientMode: boolean = false
    ): GetTemplateResult {
        if (!currentTemplateName || currentTemplateName.trim() === '') {
            throw new Error('currentTemplateName must not be empty - it\'s required for component reuse pattern detection');
        }
        if (!appFileOrPlaceholderOrSlotholderName || appFileOrPlaceholderOrSlotholderName.trim() === '') {
            throw new Error('appFileOrPlaceholderOrSlotholderName must not be empty - it must be provided by the caller');
        }

        if (Logger.getLogLevel() <= LogLevel.DEBUG) {
            Logger.debug(`getTemplate: appSite='${appSite}', currentTemplate='${currentTemplateName}', name='${appFileOrPlaceholderOrSlotholderName}', appView='${appView}'`, 'LoaderPreProcessJson');
        }

        // === 1. SKIP MARKER CHECK ===
        if (appFileOrPlaceholderOrSlotholderName.startsWith('!')) {
            return { htmlJson: this._createDefaultHtmlJson(this._config!.defaultSkipHtml), dataJson: null, resolvedTemplateName: this._config!.defaultSkipTemplateName, resolvedAppSite: appSite, templateInfo: null };
        }

        // === 2. O(1) COMPONENT INDEX LOOKUP ===
        const templateInfo = this.getComponentFromIndex(appSite, appView, appFileOrPlaceholderOrSlotholderName);
        if (templateInfo) {
            let htmlJson = this.getHtmlJson(templateInfo.templateAppSite, templateInfo.templateName);
            if (!htmlJson) {
                return { htmlJson: this._createDefaultHtmlJson(this._config!.defaultReplaceHtml), dataJson: null, resolvedTemplateName: this._config!.defaultReplaceTemplateName, resolvedAppSite: appSite, templateInfo: null };
            }

            const isReuse = templateInfo.placeholderName &&
                templateInfo.placeholderName.toLowerCase() !== templateInfo.templateName.toLowerCase();

            let dataJson = isReuse
                ? this.getDataJson(appSite, appFileOrPlaceholderOrSlotholderName)
                : this.getDataJson(templateInfo.templateAppSite, templateInfo.templateName);

            let resolvedDataJson: JsonObject | null = null;
            if (dataJson) {
                const dynamicDataAppSite = this._config!.dynamicDataUseResolvedAppSite ? templateInfo.templateAppSite : appSite;
                resolvedDataJson = this._getTemplateDataJsonWithInheritance(dynamicDataAppSite, appView || '', appFileOrPlaceholderOrSlotholderName, dataJson, templateInfo.templateName, flowStack, templateInfo.templateAppSite, configJson, htmlJson, clientMode);
            }

            return { htmlJson, dataJson: resolvedDataJson, resolvedTemplateName: templateInfo.templateName, resolvedAppSite: templateInfo.templateAppSite, templateInfo };
        }

        // === 3-6. FALLBACK RESOLUTION ===
        const { baseComponentName, isReuseComponent } = this.getBaseComponentName(appFileOrPlaceholderOrSlotholderName, currentTemplateName);
        const reuseJsonName = isReuseComponent ? appFileOrPlaceholderOrSlotholderName : null;

        if (Logger.getLogLevel() <= LogLevel.DEBUG && isReuseComponent) {
            Logger.debug(`getTemplate: Component reuse - Original: '${appFileOrPlaceholderOrSlotholderName}', Base: '${baseComponentName}'`, 'LoaderPreProcessJson');
        }

        // === 3. APPVIEW FALLBACK ===
        if (appView && this._config!.defaultAppViewPrefix &&
            baseComponentName.toLowerCase().includes(this._config!.defaultAppViewPrefix.toLowerCase())) {
            const appViewTemplate = replaceCaseInsensitive(baseComponentName, this._config!.defaultAppViewPrefix, appView);
            const htmlJson = this.getHtmlJson(appSite, appViewTemplate);
            if (htmlJson) {
                const dataJson = reuseJsonName
                    ? this.getDataJson(appSite, reuseJsonName)
                    : this.getDataJson(appSite, appViewTemplate);
                const resolvedName = reuseJsonName ? appFileOrPlaceholderOrSlotholderName : appViewTemplate;

                return this._finalizeTemplate(appSite, appView, appFileOrPlaceholderOrSlotholderName, currentTemplateName, appFileOrPlaceholderOrSlotholderName, htmlJson, dataJson, resolvedName, appSite, flowStack, configJson, clientMode);
            }
        }

        // === 4. PRIMARY APPSITE LOOKUP ===
        const primaryHtmlJson = this.getHtmlJson(appSite, baseComponentName);
        if (primaryHtmlJson) {
            const dataJson = reuseJsonName
                ? this.getDataJson(appSite, reuseJsonName)
                : this.getDataJson(appSite, baseComponentName);
            const resolvedName = reuseJsonName ? appFileOrPlaceholderOrSlotholderName : baseComponentName;

            return this._finalizeTemplate(appSite, appView, appFileOrPlaceholderOrSlotholderName, currentTemplateName, appFileOrPlaceholderOrSlotholderName, primaryHtmlJson, dataJson, resolvedName, appSite, flowStack, configJson, clientMode);
        }

        // === 5. SEARCHAPPSITES FALLBACK ===
        const foundSearchAppSites = this.getSearchAppSitesForAppSite(appSite);
        if (foundSearchAppSites) {
            const searchArray = foundSearchAppSites.split(',');
            for (const searchAppSite of searchArray) {
                const trimmed = searchAppSite.trim();
                if (!trimmed) continue;

                const fallbackHtmlJson = this.getHtmlJson(trimmed, baseComponentName);
                if (fallbackHtmlJson) {
                    const dataJson = reuseJsonName
                        ? this.getDataJson(appSite, reuseJsonName)
                        : this.getDataJson(trimmed, baseComponentName);
                    const resolvedName = reuseJsonName ? appFileOrPlaceholderOrSlotholderName : baseComponentName;

                    return this._finalizeTemplate(appSite, appView, appFileOrPlaceholderOrSlotholderName, currentTemplateName, appFileOrPlaceholderOrSlotholderName, fallbackHtmlJson, dataJson, resolvedName, trimmed, flowStack, configJson, clientMode);
                }
            }
        }

        // === 6. NOT FOUND ===
        if (Logger.getLogLevel() <= LogLevel.DEBUG) {
            Logger.debug(`getTemplate: Not found '${appFileOrPlaceholderOrSlotholderName}' (base: '${baseComponentName}')`, 'LoaderPreProcessJson');
        }

        return { htmlJson: this._createDefaultHtmlJson(this._config!.defaultReplaceHtml), dataJson: null, resolvedTemplateName: this._config!.defaultReplaceTemplateName, resolvedAppSite: appSite, templateInfo: null };
    }

    _createDefaultHtmlJson(content: string): JsonObject {
        if (!content) {
            return new JsonObject();
        }

        const meta = {
            hasJsonKeys: false,
            jsonKeys: [] as string[],
            hasPlaceholders: false,
            placeholders: [] as string[],
            hasSlotholders: false,
            slotholders: [] as string[],
            hasArrayBlocks: false,
            arrayBlocks: [] as string[]
        };
        return new JsonObject({ '_meta': meta, 's1': content });
    }

    _finalizeTemplate(
        appSite: string,
        appView: string | null,
        appFile: string,
        _currentTemplateName: string,
        _componentName: string,
        htmlJson: JsonObject,
        dataJson: JsonObject | null,
        templateKey: string,
        resolvedAppSite: string,
        flowStack: string[] | null,
        configJson: JsonObject | null = null,
        clientMode: boolean = false
    ): GetTemplateResult {
        let resolvedDataJson: JsonObject | null = null;
        if (dataJson) {
            const dynamicDataAppSite = this._config!.dynamicDataUseResolvedAppSite ? resolvedAppSite : appSite;
            resolvedDataJson = this._getTemplateDataJsonWithInheritance(dynamicDataAppSite, appView || '', appFile, dataJson, templateKey, flowStack, resolvedAppSite, configJson, htmlJson, clientMode);
        }

        return { htmlJson, dataJson: resolvedDataJson, resolvedTemplateName: templateKey, resolvedAppSite, templateInfo: null };
    }

    getAppSiteMetadata(appSite: string, appView: string | null = null): AppSiteMetadata | null {
        const key = !appView ? appSite : `${appSite}_${appView}`;
        return this._store.tryGetMetadata(key) || null;
    }

    getHtmlJson(appSite: string, templateName: string): JsonObject | null {
        if (this._htmlJsonCache.has(appSite)) {
            const templates = this._htmlJsonCache.get(appSite)!;
            for (const [key, value] of templates.entries()) {
                if (key.toLowerCase() === templateName.toLowerCase()) {
                    return value.jsonStructure as unknown as JsonObject;
                }
            }
        }
        return null;
    }

    getDataJson(appSite: string, templateName: string, flowStack: string[] | null = null): JsonObject | null {
        if (this._dataJsonCache.has(appSite)) {
            const templates = this._dataJsonCache.get(appSite)!;
            const dataJsonKey = `${appSite.toLowerCase()}_${templateName.toLowerCase()}`;
            if (templates.has(dataJsonKey)) {
                const value = templates.get(dataJsonKey)!;
                if (!flowStack || flowStack.length === 0) {
                    return value.jsonStructure;
                }
                return this._getTemplateDataJsonWithInheritance(appSite, '', templateName, value.jsonStructure, templateName, flowStack, null, null, this.getHtmlJson(appSite, templateName));
            }
        }
        return null;
    }

    getAllTemplateNames(appSite: string): string[] {
        const templates: string[] = [];
        if (this._htmlJsonCache.has(appSite)) {
            const appSiteTemplates = this._htmlJsonCache.get(appSite)!;
            for (const key of appSiteTemplates.keys()) {
                templates.push(key);
            }
        }
        return templates;
    }

    get defaultAppFile(): string { return this._config!.defaultAppFile; }
    declare readonly defaultAppViewPrefix: string;

    // skipDynamicView/skipDynamicData property accessors are attached from
    // loaderPreProcessJson_Accessors.ts (mirrors C# LoaderPreProcessJson_Accessors.cs partial-class file).
    declare skipDynamicView: string | null;
    declare skipDynamicData: string | null;

    getBaseComponentName(placeholderOrSlotholderName: string, currentTemplateName: string): { baseComponentName: string; isReuseComponent: boolean } {
        const parts = placeholderOrSlotholderName.split('_');
        if (parts.length >= 3 && currentTemplateName) {
            for (let i = 1; i < parts.length - 1; i++) {
                if (!isNaN(parseInt(parts[i]))) {
                    const prefixParts: string[] = [];
                    for (let j = 0; j < i; j++) {
                        prefixParts.push(parts[j]);
                    }
                    const prefix = prefixParts.join('_');

                    if (prefix.toLowerCase() === currentTemplateName.toLowerCase()) {
                        const remainingParts: string[] = [];
                        for (let j = i + 1; j < parts.length; j++) {
                            remainingParts.push(parts[j]);
                        }
                        const baseComponentName = remainingParts.join('_');
                        return { baseComponentName, isReuseComponent: true };
                    }
                }
            }
        }

        return { baseComponentName: placeholderOrSlotholderName, isReuseComponent: false };
    }

    private _deserializeAppSiteMetadata(raw: PlainJsonObject): AppSiteMetadata {
        const metadata = new AppSiteMetadata();
        metadata.appSiteName = typeof raw.appSiteName === 'string' ? raw.appSiteName : '';
        metadata.appFile = typeof raw.appFile === 'string' ? raw.appFile : '';
        metadata.searchAppSites = typeof raw.searchAppSites === 'string' ? raw.searchAppSites : '';
        metadata.appView = typeof raw.appView === 'string' ? raw.appView : '';
        metadata.appViews = Array.isArray(raw.appViews)
            ? raw.appViews.filter((v): v is string => typeof v === 'string')
            : [];
        metadata.rootComponent = raw.rootComponent && typeof raw.rootComponent === 'object'
            ? this._deserializeTemplateInfo(raw.rootComponent as PlainJsonObject)
            : null;
        return metadata;
    }

    private _deserializeTemplateInfo(raw: PlainJsonObject): TemplateInfo {
        const info = new TemplateInfo();
        info.htmlFileName = typeof raw.htmlFileName === 'string' ? raw.htmlFileName : '';
        info.htmlRelativePath = typeof raw.htmlRelativePath === 'string' ? raw.htmlRelativePath : '';
        info.jsonFileName = typeof raw.jsonFileName === 'string' ? raw.jsonFileName : '';
        info.placeholderName = typeof raw.placeholderName === 'string' ? raw.placeholderName : '';
        info.templateName = typeof raw.templateName === 'string' ? raw.templateName : '';
        info.templateAppSite = typeof raw.templateAppSite === 'string' ? raw.templateAppSite : '';
        info.appFileName = typeof raw.appFileName === 'string' ? raw.appFileName : '';
        info.templateType = typeof raw.templateType === 'string' ? raw.templateType : '';
        info.placeholders = this._deserializeStringArray(raw.placeholders);
        info.nonPlaceholders = this._deserializeStringArray(raw.nonPlaceholders);
        info.slots = this._deserializeStringArray(raw.slots);
        info.jsonKeys = this._deserializeStringArray(raw.jsonKeys);
        info.jsonArrayKeys = this._deserializeStringArray(raw.jsonArrayKeys);
        info.nonJsonKeys = this._deserializeStringArray(raw.nonJsonKeys);
        info.nonJsonArrayKeys = this._deserializeStringArray(raw.nonJsonArrayKeys);
        info.conditionalKeys = this._deserializeStringArray(raw.conditionalKeys);
        info.slotholders = this._deserializeSlotInfoArray(raw.slotholders);
        info.nonSlotholders = this._deserializeSlotInfoArray(raw.nonSlotholders);
        info.jsonInheritKeys = this._deserializeStringMap(raw.jsonInheritKeys);
        info.appViewPlaceholders = this._deserializeStringMap(raw.appViewPlaceholders);
        info.components = Array.isArray(raw.components)
            ? raw.components
                .filter((v): v is PlainJsonObject => !!v && typeof v === 'object')
                .map(v => this._deserializeTemplateInfo(v))
            : [];
        info.hasDynamicData = raw.hasDynamicData === true;
        info.hasDynamicView = raw.hasDynamicView === true;
        info.isStaticHtmlOnly = raw.isStaticHtmlOnly === true;
        info.hasJsonKeys = raw.hasJsonKeys === true;
        info.hasPlaceholders = raw.hasPlaceholders === true;
        info.hasSlotholders = raw.hasSlotholders === true;
        info.hasArrayBlocks = raw.hasArrayBlocks === true;
        info.hasConditionals = raw.hasConditionals === true;
        info.requiresDataJsonMerge = raw.requiresDataJsonMerge === true;
        info.requiresContextMerge = raw.requiresContextMerge === true;
        info.requiresTemplateTraversal = raw.requiresTemplateTraversal === true;
        info.canReturnStaticHtml = raw.canReturnStaticHtml === true;
        info.entries = this._deserializeTemplateEntryInfoArray(raw.entries);
        info.jsonKeyEntries = this._deserializeJsonKeyEntryInfoArray(raw.jsonKeyEntries);
        return info;
    }

    private _deserializeTemplateEntryInfoArray(raw: unknown): TemplateEntryInfo[] {
        if (!Array.isArray(raw)) {
            return [];
        }

        return raw
            .filter((v): v is PlainJsonObject => !!v && typeof v === 'object')
            .map(v => {
                const entry = new TemplateEntryInfo();
                entry.htmlJsonKey = typeof v.htmlJsonKey === 'string' ? v.htmlJsonKey : '';
                entry.entryType = typeof v.entryType === 'string' ? v.entryType : '';
                entry.name = typeof v.name === 'string' ? v.name : '';
                entry.ordinal = typeof v.ordinal === 'number' ? v.ordinal : 0;
                entry.isSkipMarked = v.isSkipMarked === true;
                entry.hasNestedContent = v.hasNestedContent === true;
                return entry;
            });
    }

    private _deserializeJsonKeyEntryInfoArray(raw: unknown): JsonKeyEntryInfo[] {
        if (!Array.isArray(raw)) {
            return [];
        }

        return raw
            .filter((v): v is PlainJsonObject => !!v && typeof v === 'object')
            .map(v => {
                const entry = new JsonKeyEntryInfo();
                entry.htmlJsonKey = typeof v.htmlJsonKey === 'string' ? v.htmlJsonKey : '';
                entry.jsonKey = typeof v.jsonKey === 'string' ? v.jsonKey : '';
                entry.isSkipMarked = v.isSkipMarked === true;
                entry.isInherited = v.isInherited === true;
                return entry;
            });
    }

    private _deserializeSlotInfoArray(raw: unknown): SlotInfo[] {
        if (!Array.isArray(raw)) {
            return [];
        }

        return raw
            .filter((v): v is PlainJsonObject => !!v && typeof v === 'object')
            .map(v => {
                const slot = new SlotInfo();
                slot.name = typeof v.name === 'string' ? v.name : '';
                slot.slots = Array.isArray(v.slots)
                    ? v.slots
                        .filter((item): item is PlainJsonObject => !!item && typeof item === 'object')
                        .map(item => {
                            const slotContent = new SlotContentInfo();
                            slotContent.name = typeof item.name === 'string' ? item.name : '';
                            slotContent.slotholders = this._deserializeSlotInfoArray(item.slotholders);
                            return slotContent;
                        })
                    : [];
                return slot;
            });
    }

    private _deserializeStringArray(raw: unknown): string[] {
        return Array.isArray(raw) ? raw.filter((v): v is string => typeof v === 'string') : [];
    }

    private _deserializeStringMap(raw: unknown): Map<string, string> {
        const result = new Map<string, string>();
        if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
            return result;
        }

        for (const [key, value] of Object.entries(raw as PlainJsonObject)) {
            if (typeof value === 'string') {
                result.set(key, value);
            }
        }

        return result;
    }

    _loadTemplatesFromSingleAppSite(config: IConfig, appSiteName: string, sourceHtmlCache: Map<string, Map<string, SourceFile>>, sourceJsonCache: Map<string, Map<string, TargetFile>>): void {
        const contentNames = config.getAllContentNames(appSiteName);
        if (contentNames.length === 0) {
            Logger.warn(`No content names found for AppSite: ${appSiteName}`, 'LoaderPreProcessJson');
            return;
        }

        if (Logger.getLogLevel() <= LogLevel.DEBUG) {
            Logger.debug(`Loading templates for AppSite: ${appSiteName} (${contentNames.length} files)`, 'LoaderPreProcessJson');
        }

        sourceHtmlCache.set(appSiteName, new Map());
        sourceJsonCache.set(appSiteName, new Map());

        for (const contentName of contentNames) {
            if (CommonUtil.isDynamicOrGlobalConfigName(contentName)) continue;

            const relativePath = config.getRelativeInfo(appSiteName, contentName) || '';

            const htmlContent = config.getHtmlContent(appSiteName, contentName);
            if (htmlContent != null) {
                const sourceFile = new SourceFile();
                sourceFile.relativePath = relativePath;
                sourceFile.content = htmlContent;
                sourceHtmlCache.get(appSiteName)!.set(contentName, sourceFile);
                if (Logger.getLogLevel() <= LogLevel.DEBUG) {
                    Logger.debug(`Loaded HTML for ${appSiteName}/${contentName} (size: ${htmlContent.length})`, 'LoaderPreProcessJson');
                }
            }

            const jsonData = config.getDataJson(appSiteName, contentName);
            if (jsonData) {
                const targetFile = new TargetFile();
                targetFile.relativePath = relativePath;
                targetFile.jsonStructure = jsonData;
                sourceJsonCache.get(appSiteName)!.set(contentName, targetFile);
                if (Logger.getLogLevel() <= LogLevel.DEBUG) {
                    Logger.debug(`Loaded JSON for ${appSiteName}/${contentName}`, 'LoaderPreProcessJson');
                }
            }
        }

        if (Logger.getLogLevel() <= LogLevel.DEBUG) {
            Logger.debug(`Loaded ${sourceHtmlCache.get(appSiteName)!.size} HTML and ${sourceJsonCache.get(appSiteName)!.size} JSON files for ${appSiteName}`, 'LoaderPreProcessJson');
        }
    }

    _getTemplateDataJsonWithInheritance(appSite: string, appView: string, appFile: string, dataJson: JsonObject, resolvedTemplateName: string, flowStack: string[] | null = null, resolvedAppSite: string | null = null, configJson: JsonObject | null = null, htmlJson: JsonObject | null = null, clientMode: boolean = false): JsonObject | null {
        const resolvedJson = getTemplateDataWithInheritance(appSite, appView, appFile, dataJson, resolvedTemplateName, flowStack, this, resolvedAppSite, configJson, htmlJson, clientMode);
        return resolvedJson;
    }

    getAndClearInheritanceOperations(): InheritanceOperation[] {
        const ops = this._inheritanceOperations;
        this._inheritanceOperations = [];
        return ops;
    }

    getAndClearNavigationTrace(): NavigationDecision[] {
        const trace = this._lastNavigationTrace;
        this._lastNavigationTrace = [];
        return trace;
    }

    getDynamicDataTiming(): { nanos: number; count: number } {
        const result = { nanos: this._dynamicDataNanos, count: this._dynamicDataCount };
        this._dynamicDataNanos = 0;
        this._dynamicDataCount = 0;
        return result;
    }

}

// Apply ILoader interface methods from split file (matching C# partial class pattern)
// Use defineProperties to properly transfer getters (Object.assign would invoke them)
Object.defineProperties(LoaderPreProcessJson.prototype, Object.getOwnPropertyDescriptors(iloaderMethods));
Object.assign(LoaderPreProcessJson.prototype, lifecycleMethods);
// accessorMethods uses Object.defineProperties via Object.getOwnPropertyDescriptors so
// property accessor pairs (get/set skipDynamicView etc.) are preserved on the prototype.
Object.defineProperties(LoaderPreProcessJson.prototype, Object.getOwnPropertyDescriptors(accessorMethods));
Object.assign(LoaderPreProcessJson, accessorStaticMethods);

// Apply dynamic methods from dynamicMethods
Object.assign(LoaderPreProcessJson.prototype, dynamicMethods);
